import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient } from "@angular/common/http";
import { tap } from 'rxjs/operators';
import { ApplicationUserModel, ApplicationUserFacade } from "../../akita/index";
import { PersistenceStorageService } from "../persistence-storage/persistence-storage.service";
import { AppToken } from "../../../models/app-token";
import { AppConfigProvider } from "../../../app-config.provider";

export class AuthenticationResult {
  isAuthenticated: boolean;
  isLocked: boolean;
  isRegistered: boolean;
  roles: Array<string>;
  claims: any;
  user: any;
  message: string;

  error: string;
}

export class UpdatePasswordResult {
  succeed: boolean;
  error: string;
}


@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  private authenticationResult: AuthenticationResult;
  accessClaimKey: string = "client-admin-access";

  constructor(
    private readonly httpClient: HttpClient,
    private readonly applicationUserFacade: ApplicationUserFacade,
    private readonly persistenceStorageService: PersistenceStorageService,
    private readonly appConfigProvider: AppConfigProvider
  ) { }

  isAuthorized(): Observable<AuthenticationResult> {
    if (this.authenticationResult && this.authenticationResult.isAuthenticated) {
      return of(this.authenticationResult);
    }

    return this.httpClient.post<AuthenticationResult>('/api/o/auth/isAuthenticated', {})
      .pipe(
        tap(v => {

          if (v.isAuthenticated) {

            const claims = v.user.data.claims;

            if (!claims || !claims[this.accessClaimKey]) {
              v.user = undefined;
              v.isAuthenticated = false;
              return;
            }

            const hasAccess = claims[this.accessClaimKey] as boolean;

            if (!hasAccess) {
              v.user = undefined;
              v.isAuthenticated = false;
              return;
            }

            this.storeUserInfo(v);

          } else {
            this.persistenceStorageService.remove(AppToken.APPLICATION_CURRENT_USER);
          }
        })
      );
  }

  userIsAllowed(roles: string[]) {

    if (!this.appConfigProvider.authIsEnabled()) {
      return true;
    }

    if (!this.authenticationResult || !this.authenticationResult.isAuthenticated) {
      return false;
    }

    if (!roles || roles.length === 0) {
      return true;
    }

    for (let i = 0; i < roles.length; i++) {
      if (this.authenticationResult.roles.indexOf(roles[i].toUpperCase()) > -1) {
        return true;
      }
    }

    return false;
  }

  userHasRole(role: string): boolean {
    if (!this.appConfigProvider.authIsEnabled()) {
      return true;
    }

    if (!this.authenticationResult.isAuthenticated) {
      this.persistenceStorageService.remove(AppToken.APPLICATION_CURRENT_USER);
      return false;
    }

    return this.authenticationResult.roles.indexOf(role.toUpperCase()) > -1;
  }

  getUserRoles(): Array<string> {
    if (!this.authenticationResult.isAuthenticated) {
      this.persistenceStorageService.remove(AppToken.APPLICATION_CURRENT_USER);
      return [];
    }

    return this.authenticationResult.roles;
  }

  getUserClaim(claim: string): any {
    if (!this.authenticationResult.isAuthenticated) {
      this.persistenceStorageService.remove(AppToken.APPLICATION_CURRENT_USER);
      return null;
    }

    return this.authenticationResult.claims[claim];
  }

  login(email: string, password: string): Observable<AuthenticationResult> {
    if (this.authenticationResult && this.authenticationResult.isAuthenticated) {
      //check user role/claims : hasAdminAccess
      return of(this.authenticationResult);
    }
    return this.httpClient
      .post<AuthenticationResult>('/api/o/auth/login', { email: email, password: password });
  }

  register(email: string, password: string): Observable<AuthenticationResult> {
    return this.httpClient
      .post<AuthenticationResult>('/api/o/auth/register', { email: email, password: password });
  }

  changePassword(email: string, password: string): Observable<AuthenticationResult> {
    return this.httpClient
      .post<AuthenticationResult>('/api/o/auth/changePassword', { email: email, password: password });
  }

  updatePassword(id: string, oldPassword: string, newPassword): Observable<UpdatePasswordResult> {
    return this.httpClient
      .post<UpdatePasswordResult>('/api/o/auth/updatePassword', { id: id, oldPassword: oldPassword, Password: newPassword });
  }

  resetPassword(userName: string, userId: string, code: string, password: string, passwordConfirmation: string): Observable<AuthenticationResult> {
    return this.httpClient
      .post<AuthenticationResult>('/api/o/auth/resetPassword', { id: userId, email: userName, code: code, password: password, passwordConfirmation: passwordConfirmation });
  }

  forgotPassword(email: string): Observable<AuthenticationResult> {
    return this.httpClient
      .post<AuthenticationResult>('/api/o/auth/forgotPassword', { email: email });
  }

  private storeUserInfo(result: AuthenticationResult): void {
    this.authenticationResult = result;
    this.authenticationResult.roles = result.user.data.roles;
    this.authenticationResult.claims = result.user.data.claims;
  }

  logout(): Observable<AuthenticationResult> {
    this.authenticationResult = null;
    return this.httpClient
      .post<AuthenticationResult>('/api/o/auth/logout', {}).pipe(
        tap(v => {
          this.persistenceStorageService.remove(AppToken.APPLICATION_CURRENT_USER);
        })
      );
  }

  getCurrentUser(): Observable<ApplicationUserModel> {

    if (!this.authenticationResult || !this.authenticationResult.isAuthenticated) {
      this.persistenceStorageService.remove(AppToken.APPLICATION_CURRENT_USER);
      return of(null);
    }

    const user = this.persistenceStorageService.get(AppToken.APPLICATION_CURRENT_USER) as ApplicationUserModel;

    if (user) {
      return of(user);
    }

    return this.applicationUserFacade.getByCode(this.authenticationResult.user.data.code).pipe(
      tap(v => {
        this.persistenceStorageService.set(AppToken.APPLICATION_CURRENT_USER, v);
      })
    ) as Observable<ApplicationUserModel>;
  }
}
